import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  MenuItem,
  Select,
  // Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/query";
import { Vessel } from "../../common/Vessel";
import { colorFromName } from "../../common/ColorFromName";
import { useGetVesselQuery, useGetVesselsQuery } from "../../redux/api/vessel";
import RosterChangeForm from "./RosterChangeForm";
import "./VesselRosterComponent.scss";
import { toast } from "react-toastify";
import useDocumentTitle from "../../util/useDocumentTitle";
import { useSelector } from "react-redux";
import { selectCurrentCaptain, selectCurrentUser } from "../../redux/authentication/authSlice";
import { UserGroup } from "../../common/User";
import RosterStatusModal from "./RosterStatusModal";
import { RosterFilter, RosterMember } from "../../common/Dispatch";
import ShortTermJobOrderModal from "./shortTermJobOrder/ShortTermJobOrderModal";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import * as XLSX from "xlsx";
import moment from "moment";
import { JobRating } from "../../common/JobOrder";
import PortCreate from "../admin/PortCreate";
const VesselRoster = () => {
  useDocumentTitle("Roster");
  const { id } = useParams();
  const navigate = useNavigate();

  const user = useSelector(selectCurrentUser);
  const isViewOnly =
    (user?.userGroup as UserGroup) === UserGroup.CREWING_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.FLEET_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.VESSEL_EXECS ||
    (user?.userGroup as UserGroup) === UserGroup.HUMAN_RESOURCES;
  const isCaptain = useSelector(selectCurrentCaptain);

  type TabValue = "crewChange" | "daysOnBoard" | "instructions" | "SIURules";
  const [currentTab /*setCurrentTab*/] = useState<TabValue>("crewChange");
  const [rosterFormKey, setRosterFormKey] = useState("a");
  const [rosterStatus, setRosterStatus] = useState<RosterMember[]>([]);
  const [rosterStatusKey, setRosterStatusKey] = useState("x");
  const [changedRowsWithError, setChangedRowsWithError] = useState<number[]>(
    []
  );
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [editingExtra, setEditingExtra] = useState(false);
  const [editingTrainingPermission, setEditingTrainingPermission] = useState(false);
  // variables to control Roster View Filter
  const [rosterFilter, setRosterFilter] = useState(RosterFilter.FULL);

  const [portOpen, setPortOpen] = useState(false);
  const { data: vesselOptions, isLoading, isFetching } = useGetVesselsQuery({});
  const { data: vessel } = useGetVesselQuery(
    id !== undefined && id !== "" && id !== "new" ? id : skipToken
  );

  const changeVessel = (vesselId: number | undefined) => {
    if (vesselId != null) {
      navigate(`/roster/${vesselId}`);
    }
  };

  useEffect(() => {
    if (vesselOptions?.length === 1) {
      changeVessel(vesselOptions[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselOptions]);

  const handleRosterStatusCallback = (rosterStatus: RosterMember[]) => {
    setRosterStatusKey(`${rosterStatusKey} === x ? y : x`);
    setRosterStatus(rosterStatus);
  };

  const convertTo2DArray = (data: RosterMember[]) => {
    const headers = [
      `Rating`,
      `Mariner's Name`,
      `Departed`,
      `Order ID`,
      `Date Joined`,
      `Current Days Onboard`,
      `Days Onboard at Signoff`,
      `Crew Change Needed`,
      `SIU Seniority`,
      `Port Relief Requested`,
      `Reason`,
      `Destination or CIL`,
      `Estimated Relief Time (hours)`,
      `Known Replacement`,
      `Onboarded`,
      `Offsigner Comments`,
      `Onsigner Comments`,
    ];

    // filter and sort if FILLED/UNFILLED
    if (rosterFilter !== RosterFilter.FULL) {
      data = data
        .filter((row) => {
          if (rosterFilter === RosterFilter.FILLED) {
            return !!row.jobOrder.onsigner;
          } else {
            return !!row.jobOrder.id && !row.jobOrder.onsigner;
          }
        })
        .sort((a: RosterMember, b: RosterMember) => {
          const fillByDateComparison = a.jobOrder.fillByDate.localeCompare(
            b.jobOrder.fillByDate
          );
          if (fillByDateComparison !== 0) {
            return fillByDateComparison;
          } else {
            return (
              Object.keys(JobRating).indexOf(a.rating) -
              Object.keys(JobRating).indexOf(b.rating)
            );
          }
        });
    }

    const rows = data.map((row) => [
      row.rating.substring(1),
      !!row.dispatch.profile &&
      [
        row.dispatch.profile.firstName,
        row.dispatch.profile.middleName,
        row.dispatch.profile.lastName,
      ].join(" "),
      row.jobOrder.offsignerDeboarded,
      row.jobOrder.id,
      row.dispatch?.startDate === null ? "" : row.dispatch?.startDate,
      !!row.currentDaysOnboard || row.currentDaysOnboard === 0
        ? row.currentDaysOnboard
        : "",
      !!row.jobOrder.fillByDate &&
        !!row.dispatch.startDate &&
        moment(row.jobOrder.fillByDate)
          .startOf("day")
          .isSameOrAfter(moment(row.dispatch.startDate).startOf("day"))
        ? Math.ceil(
          moment(row.jobOrder.fillByDate)
            .startOf("day")
            .diff(moment(row.dispatch.startDate).startOf("day"), "days")
        ) + 1
        : "",
      row.jobOrder?.fillByDate === null ? "" : row.jobOrder?.fillByDate,
      !!row.dispatch.profile ? row.dispatch.profile.seniority : "",
      row.jobOrder?.port ? row.jobOrder?.port : "",
      row.jobOrder.reliefReason === null ||
        row.jobOrder.reliefReason === undefined
        ? ""
        : row.jobOrder.reliefReason,
      row.jobOrder?.offsignerDestination === null
        ? ""
        : row.jobOrder?.offsignerDestination,
      row.jobOrder?.reliefTime || row.jobOrder?.reliefTime === 0
        ? row.jobOrder.reliefTime
        : "",
      row.offsigning
        ? [
          row.jobOrder?.onsigner?.firstName,
          row.jobOrder?.onsigner?.middleName,
          row.jobOrder?.onsigner?.lastName,
        ].join(" ")
        : "",
      row.jobOrder.onsignerOnboarded,
      row.jobOrder?.offsignerNotes === null ? "" : row.jobOrder?.offsignerNotes,
      row.jobOrder?.onsignerNotes === null ? "" : row.jobOrder?.onsignerNotes,
    ]);
    return [headers, ...rows];
  };

  const handleExcelDownload = () => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet(convertTo2DArray(rosterStatus));
    XLSX.utils.book_append_sheet(workbook, worksheet, `Roster`);
    XLSX.writeFile(
      workbook,
      !!vessel ? `${vessel.name} Roster.xlsx` : `Roster.xlsx`
    );
  };

  const handleCallback = (saveResult: {
    data: {
      successCount: number;
      failedCount: number;
      additionalError?: string;
    };
    hasData: boolean;
    errors: number[];
    hasError: boolean;
    hasInfo: boolean;
  }) => {
    if (saveResult.hasData && !!vessel?.id) {
      setRosterFormKey(`${rosterFormKey} === a ? b : a`);
      if (saveResult.data.successCount > 0) {
        toast.success(
          <p>
            Changes for {saveResult.data.successCount} record(s) saved
            successfully.
          </p>,
          {
            position: "top-center",
            autoClose: 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          }
        );
      }
      if (saveResult.data.failedCount > 0) {
        toast.error(
          <p>
            Changes for {saveResult.data.failedCount} could not be saved.
            {!!saveResult.data.additionalError &&
              " " + saveResult.data.additionalError}
          </p>,
          {
            position: "top-center",
            autoClose: !!saveResult.data.additionalError ? false : 5000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          }
        );
      }
    }
    if (saveResult.hasError) {
      setChangedRowsWithError(saveResult.errors);
      toast.error(<p>Required fields are missing.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
    if (saveResult.hasInfo) {
      setChangedRowsWithError([]);
      toast.info(<p>No Job Order changes to save.</p>, {
        position: "top-center",
        autoClose: 5000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
    }
    setFormSubmitted(false);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      {!isLoading && !isFetching && (
        <>
          <div className="roster-page-header mb-0">
            <div className="top-row">
              <div className="header-group">
                <h1>Roster</h1>
                <Tooltip
                  title={
                    !!vessel
                      ? vessel?.defaultAssignee?.name ||
                      vessel?.defaultAssignee?.email ||
                      ""
                      : "Default Assignee"
                  }
                  placement="right"
                >
                  <Avatar
                    sx={{
                      bgcolor: colorFromName(vessel?.defaultAssignee?.name || ""),
                      height: 30,
                      width: 30,
                    }}
                  >
                    {vessel?.defaultAssignee?.name?.substring(0, 1)}
                  </Avatar>
                </Tooltip>
                {!!vesselOptions?.length && vesselOptions?.length === 1 && (
                  <h3>{vessel?.name}</h3>
                )}
                {!!vesselOptions?.length && vesselOptions?.length > 1 && (
                  <Autocomplete
                    className="vessel-dropdown"
                    id="vesselOptions"
                    value={vessel ? vessel : null}
                    options={
                      vesselOptions
                        ? (vesselOptions as Vessel[]).filter((v) => !v.disabled)
                        : []
                    }
                    getOptionLabel={(opt) => (opt.name ? opt.name : "")}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Pick a Vessel" />
                    )}
                    onChange={(e, val) => changeVessel(val?.id)}
                  />
                )}
              </div>
              {isCaptain && <div className="disclaimer">
                <b>PERSONAL INFORMATION DISCLAIMER:</b>
                The personal information displayed on this system is intended solely for authorized business purposes.
                Access, use, disclosure, or distribution of this information is limited to individuals with a legitimate
                business need and must comply with applicable privacy laws, company policies, and data protection standards.
                Unauthorized access, misuse, or sharing of personal information may result in disciplinary action and/or legal consequences.
                Immediately notify, Dan Uhlir, Director of Human Resources at TOTE Services if you suspect or discover that
                personal information on this system has been lost, stolen, or compromised.
              </div>}
            </div>
            <div className="header-button">
              {!isViewOnly && (
                <Button
                  form="crewChangeForm"
                  variant="contained"
                  className="blue-button wd-auto ht-4px"
                  type="submit"
                  disabled={formSubmitted}
                >
                  {" "}
                  {formSubmitted === true ? (
                    <CircularProgress className="flex align-items-center color-white wd-2px" />
                  ) : (
                    "Save Changes"
                  )}
                </Button>
              )}
              {!!vessel && (
                <RosterStatusModal
                  rosterStatusData={rosterStatus}
                  vessel={vessel}
                  key={rosterStatusKey}
                >
                  <Button className="blue-outline-button">Offsign List</Button>
                </RosterStatusModal>
              )}
              {!!vessel && (
                <div className="unfilled-filter">
                  <Select
                    id="rosterFilter"
                    className="blue-outline-button  py-4 px-8 ht-4px"
                    value={rosterFilter}
                    onChange={(e) =>
                      setRosterFilter(e.target.value as RosterFilter)
                    }
                    IconComponent={() => <FilterAltIcon />}
                  >
                    <MenuItem value={RosterFilter.FULL}>Full List</MenuItem>
                    <MenuItem value={RosterFilter.UNFILLED}>Unfilled</MenuItem>
                    <MenuItem value={RosterFilter.FILLED}>Filled</MenuItem>
                  </Select>
                </div>
              )}
              {!!vessel &&
                ((user?.userGroup as UserGroup) ===
                  UserGroup.CREWING_MANAGERS ||
                  (user?.userGroup as UserGroup) ===
                  UserGroup.HUMAN_RESOURCES_ADMINS ||
                  (user?.userGroup as UserGroup) ===
                  UserGroup.CREWING_SPECIALISTS ||
                  (user?.userGroup as UserGroup) === 
                  UserGroup.CREWING_EVAL_SPECIALISTS) && (
                  <>
                    <ShortTermJobOrderModal vessel={vessel}>
                      <Button className="blue-outline-button">
                        Short-Term Fill
                      </Button>
                    </ShortTermJobOrderModal>
                    <Button
                      onClick={() => setEditingExtra(!editingExtra)}
                      className="blue-outline-button"
                    >
                      Mark Extra
                    </Button>
                    <Button
                      onClick={() => setEditingTrainingPermission(!editingTrainingPermission)}
                      className="blue-outline-button"
                    >
                      Training Permission
                    </Button>
                  </>
                )}
              {((user?.userGroup as UserGroup) === UserGroup.CREWING_MANAGERS ||
                (user?.userGroup as UserGroup) ===
                UserGroup.HUMAN_RESOURCES_ADMINS) && (
                  <>
                    <Button
                      className="blue-outline-button"
                      onClick={() => setPortOpen(true)}
                    >
                      Add Port
                    </Button>
                    <Dialog open={portOpen} onClose={() => setPortOpen(false)}>
                      <PortCreate
                        vessels={!!vessel ? [vessel] : []}
                        onClose={setPortOpen}
                      />
                    </Dialog>
                  </>
                )}
              {!!vessel && (
                <>
                  <Button
                    className="blue-button"
                    onClick={() => handleExcelDownload()}
                  >
                    Download Excel
                  </Button>
                </>
              )}
            </div>
            {/* <Tabs
              value={currentTab}
              onChange={(e, newTab) => setCurrentTab(newTab)}
              aria-label="Roster Page Tabs"
              className="top-right-tabs"
            >
              <Tab value="crewChange" label="Crew Change" />
              <Tab value="daysOnBoard" label="Days Onboard" />
              <Tab value="instructions" label="Instructions" />
              <Tab value="SIURules" label="SIU Rules"></Tab>
            </Tabs> */}
          </div>
          <div
            style={{ display: currentTab === "crewChange" ? "block" : "none" }}
          >
            <RosterChangeForm
              parentCallback={handleCallback}
              rosterStatusCallback={handleRosterStatusCallback}
              key={rosterFormKey}
              changedRowsWithError={changedRowsWithError}
              formSubmitted={formSubmitted}
              setFormSubmitted={setFormSubmitted}
              editingExtra={editingExtra}
              setEditingExtra={setEditingExtra}
              editingTrainingPermission={editingTrainingPermission}
              setEditingTrainingPermission={setEditingTrainingPermission}
              filter={rosterFilter}
            />
          </div>
          <div
            style={{ display: currentTab === "daysOnBoard" ? "block" : "none" }}
          >
            Days Onboard. Content Coming Soon.
          </div>
          <div
            style={{
              display: currentTab === "instructions" ? "block" : "none",
            }}
          >
            Instructions. Content Coming Soon.
          </div>
          <div
            style={{ display: currentTab === "SIURules" ? "block" : "none" }}
          >
            SIU Rules. Content Coming Soon.
          </div>
        </>
      )}
    </>
  );
};

export default VesselRoster;
